import { useAtomValue } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import React, { useEffect, useRef, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { ignoreBPAControlFindings } from 'views/BPAFindings/services/bpaFindingsService';
import { companyIdAtom } from 'views/state';

import { BPATopControlItem } from '../../types';

interface Props {
  control: BPATopControlItem;
  providerCodes: string[];
  integrationIds: number[];
  onClose: () => void;
  onComplete: () => void;
}

const IGNORE_MESSAGE_MAX_LENGTH = 500;

const BPAControlIgnoreModal = ({
  control,
  providerCodes,
  integrationIds,
  onClose,
  onComplete,
}: Props) => {
  const companyId = useAtomValue(companyIdAtom);
  const isMountedRef = useRef(true);
  const [ignoredMessage, setIgnoredMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const trimmedIgnoredMessage = ignoredMessage.trim();

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const handleSubmit = async () => {
    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before ignoring findings.');
      return;
    }
    if (!trimmedIgnoredMessage) {
      const validationMessage =
        'Comments are required before ignoring BPA control findings.';
      setError(validationMessage);
      showFailureToast(validationMessage);
      return;
    }

    setLoading(true);
    setError('');
    let completed = false;

    try {
      const response = await ignoreBPAControlFindings({
        company_id: companyId,
        control_id: control.control_pk,
        provider_codes: providerCodes,
        integration_ids: integrationIds,
        ignored_message: trimmedIgnoredMessage,
      });
      if (!isMountedRef.current) return;

      if (response.complete) {
        showSuccessToast(response.message);
      } else {
        showWarningToast(
          response.message ||
            `Ignored ${response.updated_count} of ${response.requested_count} BPA findings.`,
        );
      }
      completed = true;
    } catch (err) {
      if (!isMountedRef.current) return;
      const failureMessage = extractApiErrorMessage(
        err,
        'Failed to ignore BPA control findings.',
      );
      setError(failureMessage);
      showFailureToast(failureMessage);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        if (completed) onComplete();
      }
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md" zIndex={10001}>
      <ModalHeader toggle={onClose}>Ignore BPA Control</ModalHeader>
      <ModalBody className="pb-2">
        <Card className="mb-3">
          <CardBody className="py-3">
            <dl className="row mb-0">
              <dt className="col-sm-4">Control ID</dt>
              <dd className="col-sm-8">{control.control_pk}</dd>
              <dt className="col-sm-4">Name</dt>
              <dd className="col-sm-8">{control.description}</dd>
              <dt className="col-sm-4">Benchmark</dt>
              <dd className="col-sm-8">{control.benchmark_description}</dd>
              <dt className="col-sm-4">Provider</dt>
              <dd className="col-sm-8">{control.provider}</dd>
              <dt className="col-sm-4">Severity</dt>
              <dd className="col-sm-8">{control.severity_label}</dd>
              <dt className="col-sm-4">Active Findings</dt>
              <dd className="col-sm-8 mb-0">{control.active_count}</dd>
            </dl>
          </CardBody>
        </Card>

        <p className="text-warning">
          This will ignore active findings for this control within the current
          dashboard provider and integration scope. Fixed and already ignored
          findings will not be changed.
        </p>

        <label htmlFor={`bpa_control_ignore_message_${control.control_pk}`}>
          <strong>Comments</strong>
          <span className="text-danger"> *</span>
        </label>
        <Input
          id={`bpa_control_ignore_message_${control.control_pk}`}
          type="textarea"
          rows="3"
          maxLength={IGNORE_MESSAGE_MAX_LENGTH}
          value={ignoredMessage}
          onChange={(event) => setIgnoredMessage(event.target.value)}
          placeholder="Accepted risk, false positive, or business context"
          disabled={loading}
          required
          aria-required="true"
        />
        <div
          className={`text-right small mt-1 ${
            ignoredMessage.length >= IGNORE_MESSAGE_MAX_LENGTH
              ? 'text-warning'
              : 'text-muted'
          }`}
          aria-live="polite"
        >
          {ignoredMessage.length}/{IGNORE_MESSAGE_MAX_LENGTH}
        </div>
        {error && <div className="text-danger mt-2">{error}</div>}
      </ModalBody>
      <ModalFooter className="pt-0">
        <Button color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => void handleSubmit()}
          disabled={loading || !trimmedIgnoredMessage}
        >
          {loading ? (
            <CircleSpinner size={20} color="#ffffff" loading />
          ) : (
            'Ignore'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BPAControlIgnoreModal;
